import { Store } from "lucide-react";
import { type JSX } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { Modal } from "@/shared/ui/modal";

import { useSellerRef } from "../api/use-seller-ref";

type Marketplace = components["parameters"]["Marketplace"];

const MKP_LABEL: Record<Marketplace, string> = {
  shopee: "Shopee",
  lazada: "Lazada",
  tiktok: "TikTok",
};

interface SellerRefModalProps {
  open: boolean;
  onClose: () => void;
  sku: string;
  marketplace: Marketplace;
}

/**
 * "Check Sellername" read-only modal (PRD v1.8 §4.6.3.2-D).
 *
 * Shows the marketplace listing identity (Seller ID / Product ID /
 * Seller Name) for a (sku, marketplace). Read-only: no submit, just a Close
 * button. Handles loading, empty ("no mapping found"), generic error, and the
 * 501 "not available" state while the real adapter is a stub.
 */
export function SellerRefModal({
  open,
  onClose,
  sku,
  marketplace,
}: SellerRefModalProps): JSX.Element | null {
  const { t } = useTranslation("inventory");
  const { data, isLoading, isError, isNotImplemented, refetch } = useSellerRef(
    sku,
    marketplace,
    open,
  );

  const mkpLabel = MKP_LABEL[marketplace] ?? marketplace;
  const items = data?.items ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={560}
      title={<span data-testid="seller-ref-title">{t("seller_ref.title")}</span>}
      subtitle={t("seller_ref.subtitle", { sku, mkp: mkpLabel })}
      footer={
        <button
          type="button"
          data-testid="seller-ref-close"
          onClick={onClose}
          className="rounded-[10px] border border-[var(--border-default)] px-4 py-2.5 text-sm font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-sunken)]"
        >
          {t("seller_ref.close")}
        </button>
      }
    >
      {isLoading ? (
        <div
          className="space-y-2 py-2"
          data-testid="seller-ref-loading"
          aria-busy="true"
          aria-live="polite"
        >
          <p className="text-center text-sm text-[var(--text-subtle)]">{t("seller_ref.loading")}</p>
          {[...Array(2)].map((_, i) => (
            <div
              key={i}
              className="h-9 w-full animate-pulse rounded-lg bg-[var(--surface-sunken)]"
            />
          ))}
        </div>
      ) : isNotImplemented ? (
        <div
          className="flex flex-col items-center gap-3 px-5 py-10 text-center"
          data-testid="seller-ref-not-available"
        >
          <span className="grid h-11 w-11 place-items-center rounded-full bg-[var(--intent-neutral-bg)] text-[var(--intent-neutral-fg)]">
            <Store className="h-5 w-5" aria-hidden />
          </span>
          <p className="max-w-[380px] text-sm text-[var(--text-secondary)]">
            {t("seller_ref.not_available")}
          </p>
        </div>
      ) : isError ? (
        <div
          className="flex flex-col items-center gap-3 px-5 py-10 text-center"
          data-testid="seller-ref-error"
        >
          <p className="text-sm text-[var(--text-subtle)]">{t("seller_ref.error")}</p>
          <button
            type="button"
            data-testid="seller-ref-retry"
            onClick={() => refetch()}
            className="rounded-lg border border-[var(--premium-border)] px-4 py-1.5 text-sm font-medium transition hover:bg-[var(--canvas)]"
          >
            {t("seller_ref.retry")}
          </button>
        </div>
      ) : items.length === 0 ? (
        <p
          className="px-5 py-10 text-center text-sm text-[var(--text-subtle)]"
          data-testid="seller-ref-empty"
        >
          {t("seller_ref.empty", { mkp: mkpLabel })}
        </p>
      ) : (
        <div
          className="overflow-hidden rounded-xl border border-[var(--premium-border)]"
          data-testid="seller-ref-table"
        >
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-[var(--row-divider)] bg-[var(--surface-sunken)]">
                <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--text-subtle)]">
                  {t("seller_ref.col_seller_id")}
                </th>
                <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--text-subtle)]">
                  {t("seller_ref.col_product_id")}
                </th>
                <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--text-subtle)]">
                  {t("seller_ref.col_seller_name")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--row-divider)]">
              {items.map((item, i) => (
                <tr key={`${item.seller_id}-${item.product_id}-${i}`} data-testid="seller-ref-row">
                  <td className="px-3 py-2 font-mono text-[13px] text-[var(--text-primary)]">
                    {item.seller_id}
                  </td>
                  <td className="px-3 py-2 font-mono text-[13px] text-[var(--text-secondary)]">
                    {item.product_id}
                  </td>
                  <td className="px-3 py-2 text-[13px] text-[var(--text-primary)]">
                    {item.seller_name}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
